import { FC, useCallback, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useWorkspaceStore } from "../../../store/workspace/WorkspaceStore";
import { CompanyTypeFilterChip } from "../../../components/filters/CompanyType/CompanyTypeFilterChip";
import { DEFAULT_ENTITY_TYPE_FILTER_VALUE } from "../../../components/filters/CompanyType/constants";
import { EntityTypeFilterValue } from "../../../legacy/types/companyFilter";
import { LightTooltip } from "../../../components/LightTooltip";

/**
 * Workspace-level company type filter (GHQ / Corporate Parent / Domestic
 * Parent). Persisted on userFilters, so it drives both the Company List and the
 * TAM / SAM / SOM company counts. Rendered in the shared filter toolbar so it is
 * visible on every workspace tab.
 */
export const CompanyTypeFilter: FC = () => {
  const userFilters = useWorkspaceStore((state) => state.userFilters);
  const setUserFilters = useWorkspaceStore((state) => state.setUserFilters);

  // The chip only understands the GHQ/CP/DP flags, so derive just those.
  const companyType = useMemo<EntityTypeFilterValue>(
    () => ({
      ...DEFAULT_ENTITY_TYPE_FILTER_VALUE,
      isGhq: userFilters?.isGhq,
      isGp: userFilters?.isGp,
      isDp: userFilters?.isDp,
    }),
    [userFilters?.isGhq, userFilters?.isGp, userFilters?.isDp]
  );

  const handleChange = useCallback(
    (value: EntityTypeFilterValue) => {
      setUserFilters({
        ...(userFilters ?? {}),
        isGhq: value.isGhq,
        isGp: value.isGp,
        isDp: value.isDp,
      });
    },
    [userFilters, setUserFilters]
  );

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <CompanyTypeFilterChip value={companyType} onChange={handleChange} />
      <LightTooltip
        title={
          <Typography variant="body2">
            This filter applies to the company list and to the TAM / SAM / SOM
            company counts and total spend. It does not affect market landscape,
            segment, or competitive analysis visualizations.
          </Typography>
        }
      >
        <HelpOutlineIcon sx={{ color: "text.secondary" }} />
      </LightTooltip>
    </Stack>
  );
};
